<template>
  <div ref="setting" class="content-wrapper view-setting p-0">
    <div class="content">
      <!-- Header Section -->
      <div class="settings-header mb-4">
        <h2 class="page-title">
          <i class="fas fa-cog"></i>
          ตั้งค่าระบบ
        </h2>
        <p class="text-muted">จัดการข้อมูลคลินิก และการตั้งค่าต่างๆ ของระบบ</p>
      </div>

      <!-- Settings Tabs -->
      <b-tabs content-class="mt-4" nav-class="custom-tabs">
        <!-- Tab 1: Clinic Information -->
        <b-tab title="ข้อมูลคลินิก" active>
          <b-card class="shadow-sm border-0">
            <!-- Health-Link Connection Toggle -->
            <div class="setting-section">
              <div class="d-flex align-items-center justify-content-between mb-4">
                <div>
                  <h5 class="mb-1">เชื่อมต่อ Health-Link</h5>
                  <small class="text-muted">เปิดใช้งานการเชื่อมต่อกับระบบ Health-Link</small>
                </div>
                <b-form-checkbox v-model="isFHIRAccessEnabled" switch size="lg" @change="changeFHIR"></b-form-checkbox>
              </div>
            </div>

            <hr class="my-4" />

            <!-- Clinic Details Form -->
            <b-form ref="clinicSetting" @submit.prevent="saveSetting">
              <h5 class="section-title mb-4">
                <i class="fas fa-hospital-alt mr-2"></i>
                ข้อมูลคลินิก
              </h5>

              <b-row>
                <b-col lg="3" class="text-center mb-4">
                  <label class="d-block mb-2 font-weight-bold">โลโก้คลินิก</label>
                  <upload-avatar-form v-if="clinic.id" ref="UploadAvatar" class="mx-auto" square border size="150px"
                    :uploadMaxSize="180" :default-id="defaultContentId"
                    @callback="uploadAvatarCallback"></upload-avatar-form>
                </b-col>

                <b-col lg="9">
                  <b-form-group label="ชื่อคลินิก (ภาษาไทย)" label-for="clinic-name-th" class="mb-3">
                    <b-form-input id="clinic-name-th" v-model.trim="clinic.name" type="text"
                      placeholder="กรอกชื่อคลินิกภาษาไทย" size="lg"></b-form-input>
                  </b-form-group>

                  <b-form-group label="ชื่อคลินิก (ภาษาอังกฤษ)" label-for="clinic-name-en">
                    <b-form-input id="clinic-name-en" v-model.trim="clinic.nameEn" type="text"
                      placeholder="Enter clinic name in English" size="lg"></b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="text-right mt-4">
                <b-button type="submit" variant="primary" size="lg" class="px-4">
                  <i class="fas fa-save mr-2"></i>
                  บันทึกข้อมูลคลินิก
                </b-button>
              </div>

              <hr class="my-4" />

              <!-- Support Information Section -->
              <div class="support-info-section">
                <h5 class="section-title mb-4">
                  <i class="fas fa-headset mr-2"></i>
                  ข้อมูลสำหรับการติดต่อสนับสนุน
                </h5>
                <p class="text-muted mb-4">
                  รหัสสำหรับระบุคลินิกและสาขาของคุณเมื่อติดต่อขอความช่วยเหลือ
                </p>

                <div class="support-combined-section mb-4">
                  <h6 class="mb-3">
                    <i class="fas fa-id-card mr-2"></i>
                    Support ID
                  </h6>
                  <div class="support-code-container">
                    <b-input-group size="lg">
                      <b-input-group-prepend>
                        <b-input-group-text class="support-input-addon">
                          <i class="fas fa-fingerprint"></i>
                        </b-input-group-text>
                      </b-input-group-prepend>
                      <b-form-input :value="`${$route.params.clinicUrl}/${$route.params.branchUrl}`" readonly
                        class="bg-light font-weight-bold text-dark support-input-field"></b-form-input>
                      <b-input-group-append>
                        <div ref="copyButton" class="copy-wrapper-large support-input-addon" @click="
                          copyToClipboard(
                            `${$route.params.clinicUrl}/${$route.params.branchUrl}`,
                            'Support ID'
                          )
                          ">
                          <i class="fas fa-copy mr-2"></i>
                          <span>คัดลอก</span>
                        </div>
                        <b-popover :target="() => $refs.copyButton" placement="top" triggers="manual"
                          :show="showCopyPopover" variant="success" @hidden="showCopyPopover = false">
                          <template #title>
                            <i class="fas fa-check mr-1"></i>
                            คัดลอกสำเร็จ!
                          </template>
                          คัดลอก Support ID แล้ว
                        </b-popover>
                      </b-input-group-append>
                    </b-input-group>
                  </div>
                  <small class="text-muted mt-2 d-block">
                    <i class="fas fa-lightbulb mr-1"></i>
                    แนะนำให้ใช้ Support ID นี้เมื่อติดต่อสนับสนุน
                    เพื่อความสะดวกและรวดเร็ว
                  </small>
                </div>

                <div class="contact-instructions p-3 bg-light rounded">
                  <h6 class="mb-3">
                    <i class="fas fa-phone mr-2"></i>
                    วิธีการติดต่อสนับสนุน
                  </h6>
                  <ul class="mb-3 small">
                    <li>แจ้งรหัสรวม (Support ID) ข้างต้น</li>
                    <li>อธิบายปัญหาที่พบพร้อมรายละเอียด</li>
                    <li>ระบุเวลาที่เกิดปัญหา (ถ้ามี)</li>
                    <li>แนบภาพหน้าจอ (ถ้าเป็นไปได้)</li>
                  </ul>

                  <div class="contact-line p-2 bg-white rounded border">
                    <div class="d-flex align-items-center">
                      <i class="fab fa-line text-success fa-lg mr-2"></i>
                      <div class="flex-grow-1">
                        <strong>ติดต่อผ่าน LINE Official Account</strong>
                        <br />
                        <small class="text-muted">คลิกลิงก์เพื่อเปิดแชท LINE โดยตรง</small>
                      </div>
                      <a href="https://line.me/R/ti/p/@781qxigv" target="_blank" class="btn btn-success btn-sm">
                        <i class="fab fa-line mr-1"></i>
                        เปิด LINE
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </b-form>
          </b-card>
        </b-tab>
        <!-- Tab 2: Branch Information -->
        <b-tab title="ข้อมูลสาขา">
          <b-card class="shadow-sm border-0">
            <h5 class="section-title mb-4">
              <i class="fas fa-building mr-2"></i>
              ข้อมูลติดต่อสาขา
            </h5>

            <b-row>
              <b-col lg="6">
                <b-form-group label="ชื่อสาขา (ภาษาไทย)" class="mb-3">
                  <b-form-input v-model="branchTitle" placeholder="กรอกชื่อสาขา"></b-form-input>
                </b-form-group>

                <b-form-group label="ที่อยู่ (ภาษาไทย)" class="mb-3">
                  <b-form-textarea v-model="address" placeholder="กรอกที่อยู่สาขา" rows="3"
                    max-rows="3"></b-form-textarea>
                </b-form-group>
              </b-col>

              <b-col lg="6">
                <b-form-group label="ชื่อสาขา (ภาษาอังกฤษ)" class="mb-3">
                  <b-form-input v-model="branchTitleEn" placeholder="Enter branch name"></b-form-input>
                </b-form-group>

                <b-form-group label="ที่อยู่ (ภาษาอังกฤษ)" class="mb-3">
                  <b-form-textarea v-model="addressEn" placeholder="Enter branch address" rows="3"
                    max-rows="3"></b-form-textarea>
                </b-form-group>
              </b-col>
            </b-row>

            <hr class="my-4" />

            <h5 class="section-title mb-4">
              <i class="fas fa-address-book mr-2"></i>
              ช่องทางการติดต่อ
            </h5>

            <b-row>
              <b-col lg="6">
                <b-form-group label="เบอร์โทรศัพท์" class="mb-3">
                  <b-input-group>
                    <b-input-group-prepend>
                      <b-input-group-text><i class="fas fa-phone"></i></b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input v-model="telNumber" placeholder="0X-XXXX-XXXX"></b-form-input>
                  </b-input-group>
                </b-form-group>

                <b-form-group label="Facebook" class="mb-3">
                  <b-input-group>
                    <b-input-group-prepend>
                      <b-input-group-text><i class="fab fa-facebook"></i></b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input v-model="facebook" placeholder="facebook.com/yourpage"></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>

              <b-col lg="6">
                <b-form-group label="Line ID" class="mb-3">
                  <b-input-group>
                    <b-input-group-prepend>
                      <b-input-group-text><i class="fab fa-line"></i></b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input v-model="lineID" placeholder="@lineID"></b-form-input>
                  </b-input-group>
                </b-form-group>

                <b-form-group label="Website" class="mb-3">
                  <b-input-group>
                    <b-input-group-prepend>
                      <b-input-group-text><i class="fas fa-globe"></i></b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input v-model="website" placeholder="www.example.com"></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>

            <b-form-group label="Hospital Code (hcode)" class="mb-3">
              <b-form-input disabled v-model="hcode" class="bg-light"></b-form-input>
            </b-form-group>

            <div class="text-right mt-4">
              <b-button variant="outline-secondary" size="lg" class="mr-2 px-4" @click="resetConfig">
                <i class="fas fa-undo mr-2"></i>
                รีเซ็ต
              </b-button>
              <b-button variant="primary" size="lg" class="px-4" @click="saveConfig">
                <i class="fas fa-save mr-2"></i>
                บันทึกข้อมูลสาขา
              </b-button>
            </div>
          </b-card>
        </b-tab>
        <!-- Tab 3: Operation Settings -->
        <b-tab title="ตั้งค่าการทำงาน">
          <b-card class="shadow-sm border-0">
            <h5 class="section-title mb-4">
              <i class="fas fa-clock mr-2"></i>
              เวลาทำการและตารางนัดหมาย
            </h5>

            <b-row>
              <b-col lg="4">
                <b-form-group label="เวลาเปิดทำการ" class="mb-3">
                  <Datetime type="time" v-model="dateTimeOpening" input-class="form-control" format="HH:mm"
                    value-zone="UTC+7" :phrases="{ ok: 'ตกลง', cancel: 'ยกเลิก' }" :hour-step="1" :minute-step="30"
                    auto>
                  </Datetime>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group label="เวลาปิดทำการ" class="mb-3">
                  <Datetime type="time" v-model="dateTimeClosing" input-class="form-control" format="HH:mm"
                    value-zone="UTC+7" :phrases="{ ok: 'ตกลง', cancel: 'ยกเลิก' }" :hour-step="1" :minute-step="30"
                    auto>
                  </Datetime>
                </b-form-group>
              </b-col>

              <b-col lg="4">
                <b-form-group label="ระยะเวลาในตารางนัด" class="mb-3">
                  <b-input-group append="นาที">
                    <b-form-select v-model="$v.period.$model" :options="periodOptions"
                      :class="{ 'is-invalid': $v.period.$error }">
                      <template #first>
                        <b-form-select-option :value="null" disabled>
                          -- เลือกระยะเวลา --
                        </b-form-select-option>
                      </template>
                    </b-form-select>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>

            <hr class="my-4" />

            <h5 class="section-title mb-4">
              <i class="fas fa-coins mr-2"></i>
              ค่ามือแพทย์ (Doctor Fee)
            </h5>

            <b-row>
              <b-col lg="6">
                <b-form-group label="ค่า DF มาตรฐาน"
                  description="ใช้สำหรับสร้างรายการรักษาใหม่ (รายการเดิมไม่เปลี่ยนแปลง)" class="mb-3">
                  <b-input-group append="%">
                    <b-form-input v-model="$v.defaultDF.$model" type="number"
                      :class="{ 'is-invalid': $v.defaultDF.$error }"></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>

              <b-col lg="6">
                <b-form-group label="ค่า DF จัดฟัน"
                  description="ใช้สำหรับสร้างแพ็กเกจจัดฟันใหม่ (แพ็กเกจเดิมไม่เปลี่ยนแปลง)" class="mb-3">
                  <b-input-group append="%">
                    <b-form-input v-model="$v.defaultOrthoDF.$model" type="number"
                      :class="{ 'is-invalid': $v.defaultOrthoDF.$error }"></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>

            <hr class="my-4" />

            <!-- Room Management Section -->
            <h5 class="section-title mb-4">
              <i class="fas fa-door-open mr-2"></i>
              ห้องตรวจ
            </h5>

            <b-table
              :items="roomList"
              :fields="roomFields"
              small
              hover
              responsive
              head-variant="light"
              class="room-table"
              show-empty
              empty-text="ยังไม่มีห้องตรวจ"
            >
              <template v-slot:cell(index)="row">{{ row.index + 1 }}</template>

              <template v-slot:cell(name)="row">
                <b-form-input
                  v-if="editingRoomId === row.item.id"
                  v-model="editingRoomName"
                  size="sm"
                  @keyup.enter="saveRoom(row.item)"
                  @keyup.esc="cancelEdit"
                />
                <span v-else>{{ row.item.name }}</span>
              </template>

              <template v-slot:cell(actions)="row">
                <template v-if="editingRoomId === row.item.id">
                  <b-button size="sm" variant="success" class="mr-1" @click="saveRoom(row.item)">
                    <i class="fas fa-check"></i>
                  </b-button>
                  <b-button size="sm" variant="secondary" @click="cancelEdit">
                    <i class="fas fa-times"></i>
                  </b-button>
                </template>
                <template v-else-if="isManager">
                  <b-button size="sm" variant="outline-secondary" class="mr-1" @click="startEdit(row.item)">
                    <i class="fas fa-edit"></i>
                  </b-button>
                  <b-button v-if="false" size="sm" variant="outline-danger" @click="deleteRoom(row.item)">
                    <i class="fas fa-trash-alt"></i>
                  </b-button>
                </template>
              </template>
            </b-table>

            <b-row v-if="isManager" class="mt-3">
              <b-col cols="8" md="9">
                <b-form-input
                  v-model="newRoomName"
                  placeholder="กรอกชื่อห้อง..."
                  @keyup.enter="addRoom"
                />
              </b-col>
              <b-col cols="4" md="3">
                <b-button
                  variant="primary"
                  block
                  @click="addRoom"
                  :disabled="!newRoomName.trim()"
                >
                  <i class="fas fa-plus mr-1"></i> เพิ่ม
                </b-button>
              </b-col>
            </b-row>

            <hr class="my-4" />

            <!-- Receipt Payment Method Section -->
            <h5 class="section-title mb-4">
              <i class="fas fa-credit-card mr-2"></i>
              ช่องทางชำระเงิน
            </h5>

            <!-- DentCloud Payment Section (Partial Edit: no category change, no delete) -->
            <div v-if="dentCloudPayments.length > 0" class="mb-4">
              <h6 class="text-muted mb-3">
                <i class="fas fa-cloud mr-2"></i>
                DentCloud Payment
              </h6>
              <b-table
                :items="dentCloudPayments"
                :fields="dentCloudPaymentFields"
                :tbody-tr-class="getPaymentRowClass"
                small
                hover
                responsive
                head-variant="light"
                class="receipt-payment-table"
              >
                <template v-slot:cell(index)="row">{{ row.index + 1 }}</template>

                <template v-slot:cell(name)="row">
                  <b-form-input
                    v-if="editingPaymentId === row.item.id && !row.item.del"
                    v-model="editingPayment.name"
                    size="sm"
                    @keyup.enter="savePayment(row.item)"
                    @keyup.esc="cancelPaymentEdit"
                  />
                  <span v-else>
                    {{ row.item.name }}
                    <b-badge variant="info" class="ml-2">DentCloud</b-badge>
                    <b-badge v-if="row.item.del" variant="secondary" class="ml-1">ลบไปแล้ว</b-badge>
                  </span>
                </template>

                <template v-slot:cell(nameEn)="row">
                  <b-form-input
                    v-if="editingPaymentId === row.item.id"
                    v-model="editingPayment.nameEn"
                    size="sm"
                    @keyup.enter="savePayment(row.item)"
                    @keyup.esc="cancelPaymentEdit"
                  />
                  <span v-else>{{ row.item.nameEn || '-' }}</span>
                </template>

                <!-- paymentCateId: NOT editable - always show as text -->
                <template v-slot:cell(paymentCateId)="row">
                  {{ getCategoryName(row.item.paymentCateId) }}
                </template>

                <template v-slot:cell(processingFee)="row">
                  <b-input-group v-if="editingPaymentId === row.item.id" size="sm" append="%">
                    <b-form-input
                      v-model="editingPayment.processingFee"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      @keyup.enter="savePayment(row.item)"
                      @keyup.esc="cancelPaymentEdit"
                    />
                  </b-input-group>
                  <span v-else>{{ row.item.processingFee ? row.item.processingFee + '%' : '-' }}</span>
                </template>

                <template v-slot:cell(actions)="row">
                  <!-- Deleted item: no actions -->
                  <template v-if="row.item.del">
                    <!-- No actions for deleted items -->
                  </template>
                  <!-- Active item: editing mode -->
                  <template v-else-if="editingPaymentId === row.item.id">
                    <b-button size="sm" variant="success" class="mr-1" @click="savePayment(row.item)">
                      <i class="fas fa-check"></i>
                    </b-button>
                    <b-button size="sm" variant="secondary" @click="cancelPaymentEdit">
                      <i class="fas fa-times"></i>
                    </b-button>
                  </template>
                  <!-- Active item: show edit button (no delete for DentCloud) -->
                  <template v-else-if="isManager">
                    <b-button size="sm" variant="outline-secondary" @click="startPaymentEdit(row.item)">
                      <i class="fas fa-edit"></i>
                    </b-button>
                  </template>
                </template>
              </b-table>
            </div>

            <hr v-if="dentCloudPayments.length > 0" class="my-3" />

            <!-- Branch Payment Section (Editable) -->
            <h6 class="text-muted mb-3">
              <i class="fas fa-credit-card mr-2"></i>
              ช่องทางชำระเงินของสาขา
            </h6>
            <b-table
              :items="branchPayments"
              :fields="receiptPaymentFields"
              :tbody-tr-class="getPaymentRowClass"
              small
              hover
              responsive
              head-variant="light"
              class="receipt-payment-table"
              show-empty
              empty-text="ยังไม่มีช่องทางชำระเงินของสาขา"
            >
              <template v-slot:cell(index)="row">{{ row.index + 1 }}</template>

              <template v-slot:cell(name)="row">
                <b-form-input
                  v-if="editingPaymentId === row.item.id && !row.item.del"
                  v-model="editingPayment.name"
                  size="sm"
                  @keyup.enter="savePayment(row.item)"
                  @keyup.esc="cancelPaymentEdit"
                />
                <span v-else>
                  {{ row.item.name }}
                  <b-badge v-if="row.item.del" variant="secondary" class="ml-1">ลบไปแล้ว</b-badge>
                </span>
              </template>

              <template v-slot:cell(nameEn)="row">
                <b-form-input
                  v-if="editingPaymentId === row.item.id"
                  v-model="editingPayment.nameEn"
                  size="sm"
                  @keyup.enter="savePayment(row.item)"
                  @keyup.esc="cancelPaymentEdit"
                />
                <span v-else>{{ row.item.nameEn || '-' }}</span>
              </template>

              <template v-slot:cell(paymentCateId)="row">
                <b-form-select
                  v-if="editingPaymentId === row.item.id"
                  v-model="editingPayment.paymentCateId"
                  :options="paymentCategoryOptions"
                  size="sm"
                >
                  <template v-slot:first>
                    <option :value="null" disabled>-- หมวดหมู่ --</option>
                  </template>
                </b-form-select>
                <span v-else>{{ getCategoryName(row.item.paymentCateId) }}</span>
              </template>

              <template v-slot:cell(processingFee)="row">
                <b-input-group v-if="editingPaymentId === row.item.id" size="sm" append="%">
                  <b-form-input
                    v-model="editingPayment.processingFee"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    @keyup.enter="savePayment(row.item)"
                    @keyup.esc="cancelPaymentEdit"
                  />
                </b-input-group>
                <span v-else>{{ row.item.processingFee ? row.item.processingFee + '%' : '-' }}</span>
              </template>

              <template v-slot:cell(actions)="row">
                <!-- Deleted item: no actions -->
                <template v-if="row.item.del">
                  <!-- No actions for deleted items -->
                </template>
                <!-- Active item: editing mode -->
                <template v-else-if="editingPaymentId === row.item.id">
                  <b-button size="sm" variant="success" class="mr-1" @click="savePayment(row.item)">
                    <i class="fas fa-check"></i>
                  </b-button>
                  <b-button size="sm" variant="secondary" @click="cancelPaymentEdit">
                    <i class="fas fa-times"></i>
                  </b-button>
                </template>
                <!-- Active item: show edit/delete buttons -->
                <template v-else-if="isManager">
                  <b-button size="sm" variant="outline-secondary" class="mr-1" @click="startPaymentEdit(row.item)">
                    <i class="fas fa-edit"></i>
                  </b-button>
                  <b-button size="sm" variant="outline-danger" @click="deletePayment(row.item)">
                    <i class="fas fa-trash-alt"></i>
                  </b-button>
                </template>
              </template>
            </b-table>

            <b-row v-if="isManager" class="mt-3">
              <b-col cols="12" md="3">
                <b-form-input
                  v-model="newPayment.name"
                  size="sm"
                  placeholder="ชื่อ (ไทย)"
                  @keyup.enter="addPayment"
                />
              </b-col>
              <b-col cols="12" md="2">
                <b-form-input
                  v-model="newPayment.nameEn"
                  size="sm"
                  placeholder="Name (EN)"
                  @keyup.enter="addPayment"
                />
              </b-col>
              <b-col cols="12" md="3">
                <b-form-select
                  v-model="newPayment.paymentCateId"
                  :options="paymentCategoryOptions"
                  size="sm"
                >
                  <template v-slot:first>
                    <option :value="null">-- หมวดหมู่ --</option>
                  </template>
                </b-form-select>
              </b-col>
              <b-col cols="12" md="2">
                <b-input-group size="sm" append="%">
                  <b-form-input
                    v-model="newPayment.processingFee"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    placeholder="0"
                    @keyup.enter="addPayment"
                  />
                </b-input-group>
              </b-col>
              <b-col cols="12" md="2">
                <b-button
                  variant="primary"
                  block
                  size="sm"
                  @click="addPayment"
                  :disabled="!newPayment.name.trim() || !newPayment.paymentCateId"
                >
                  <i class="fas fa-plus mr-1"></i> เพิ่ม
                </b-button>
              </b-col>
            </b-row>

            <div class="text-right mt-4">
              <b-button variant="outline-secondary" size="lg" class="mr-2 px-4" @click="resetConfig">
                <i class="fas fa-undo mr-2"></i>
                รีเซ็ต
              </b-button>
              <b-button variant="primary" size="lg" class="px-4" @click="saveConfig">
                <i class="fas fa-save mr-2"></i>
                บันทึกการตั้งค่า
              </b-button>
            </div>
          </b-card>
        </b-tab>
        <!-- Tab 4: LINE Integration -->
        <b-tab title="LINE Official Account">
          <b-card class="shadow-sm border-0">
            <div class="line-integration-header mb-4">
              <div class="d-flex align-items-center">
                <i class="fab fa-line fa-2x text-success mr-3"></i>
                <div>
                  <h5 class="mb-1">เชื่อมต่อ LINE Official Account</h5>
                  <p class="text-muted mb-0">
                    ตั้งค่าการเชื่อมต่อสำหรับแจ้งเตือนนัดหมายผ่าน LINE
                  </p>
                </div>
              </div>
            </div>

            <b-alert show variant="info" class="mb-4">
              <i class="fas fa-info-circle mr-2"></i>
              กรุณากรอกข้อมูลจากแหล่งที่ถูกต้องตามที่ระบุในแต่ละกลุ่ม
              เพื่อเปิดใช้งานการแจ้งเตือนอัตโนมัติ
            </b-alert>

            <!-- Group 1: LINE Login Channel -->
            <div class="setting-section mb-4">
              <div class="section-header mb-3">
                <h6 class="section-title">
                  <i class="fas fa-sign-in-alt mr-2"></i>
                  กลุ่มที่ 1: LINE Login Channel
                </h6>
                <small class="text-muted">
                  ข้อมูลจาก LINE Developers Console → LINE Login (provider) →
                  Basic Settings → Channel ID และ Channel Secret
                </small>
              </div>

              <b-form-group label="Channel ID" class="mb-3">
                <b-form-input v-model="lineChannelId" disabled
                  placeholder="กรอก Channel ID จาก LINE Login Channel"></b-form-input>
              </b-form-group>

              <b-form-group label="Channel Secret" class="mb-3">
                <b-form-input v-model="lineChannelSecret" type="password" disabled
                  placeholder="กรอก Channel Secret จาก LINE Login Channel"></b-form-input>
              </b-form-group>
            </div>

            <!-- Group 2: Messaging API -->
            <div class="setting-section mb-4">
              <div class="section-header mb-3">
                <h6 class="section-title">
                  <i class="fas fa-comments mr-2"></i>
                  กลุ่มที่ 2: Messaging API
                </h6>
                <small class="text-muted">
                  ข้อมูลจาก LINE Developers Console → Messaging API → Channel
                  Access Token (long-lived)
                </small>
              </div>

              <b-form-group label="Channel Access Token" description="Long-lived access token สำหรับส่งข้อความ"
                class="mb-3">
                <b-form-textarea v-model="lineChannelAccessToken" rows="3" disabled
                  placeholder="กรอก Channel Access Token จาก Messaging API"></b-form-textarea>
              </b-form-group>
            </div>

            <!-- Group 3: Messaging API -->
            <div class="setting-section mb-4">
              <div class="section-header mb-3">
                <h6 class="section-title">
                  <img src="/img/icons/send.svg" alt="SEND" class="mr-2" />
                  กลุ่มที่ 3: การตั้งค่าการส่งข้อความอัตโนมัติให้แก่คนไข้
                </h6>
              </div>

              <div class="setting-section">
                <div class="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <p class="mb-1">ข้อความต้อนรับเมื่อคนไข้เชื่อมต่อกับ LINE OA ของคลินิก</p>
                    <small class="text-muted">ข้อความ: ยินดีต้อนรับ คุณ {ชื่อผู้ป่วย}
                      คุณได้เปิดใช้งานการแจ้งเตือนนัดหมายผ่าน
                      LINE เรียบร้อยแล้ว</small>
                  </div>
                  <b-form-checkbox v-model="isSendLineWelcomeToNotification" switch size="lg" />
                </div>

                <hr class="my-4" />

                <div class="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <p class="mb-1">ข้อความแจ้งเตือนการนัดหมาย พร้อมลิงก์ยืนยันนัดหมาย</p>
                  </div>
                  <b-form-checkbox v-model="isSendLineConfirmationAppointmentLink" switch size="lg" />
                </div>

                <hr class="my-4" />

                <div class="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <p class="mb-1">ลิงก์ดูใบเสร็จผ่านการแสดงผลระบบ DentCare</p>
                  </div>
                  <b-form-checkbox v-model="isSendLineReceipt" switch size="lg" />
                </div>

                <hr class="my-4" />

                <div class="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <p class="mb-1">ลิงก์ประเมินความพึงพอใจ หลังบริการ</p>
                  </div>
                  <b-form-checkbox v-model="isSendLineFeedback" switch size="lg" />
                </div>
                <b-form-group :disabled="!isSendLineFeedback" class="mb-3 feedback-url-group">
                  <div class="feedback-url-wrapper">
                    <b-form-input
                      v-model="feedbackUrl"
                      placeholder="กรอกลิงก์ประเมินความพึงพอใจ หลังบริการ"
                      class="feedback-url-input"
                    />
                    <b-button
                      v-if="feedbackUrl"
                      @click="feedbackUrl = ''"
                      variant="link"
                      class="feedback-clear-button"
                      tabindex="-1"
                    >
                      <span aria-label="Clear" class="feedback-clear-button-inner">
                        <img src="/img/icons/x.svg" alt="x" class="feedback-clear-icon" />
                      </span>
                    </b-button>
                  </div>
                </b-form-group>
                <label class="text-danger" v-if="isFeedbackValidation">กรุณากรอกลิงก์ประเมินความพึงพอใจ
                  หลังบริการ</label>
              </div>
            </div>

            <div class="text-right mt-4">
              <b-button variant="outline-secondary" size="lg" class="mr-2 px-4" @click="resetConfig">
                <i class="fas fa-undo mr-2"></i>
                รีเซ็ต
              </b-button>
              <b-button variant="primary" size="lg" class="px-4" @click="saveConfig">
                <i class="fas fa-save mr-2"></i>
                บันทึกการเชื่อมต่อ LINE
              </b-button>
            </div>
          </b-card>
        </b-tab>
        <!-- Tab 5: Dentcare setting -->
        <b-tab title="การแสดงผลระบบ DentCare">
          <b-card class="shadow-sm border-0">
            <div class="line-integration-header">
              <div class="d-flex align-items-center">
                <img src="/img/icons/visibility.svg" alt="SEND" class="mr-2" />
                <div>
                  <h5 class="mb-1">การแสดงผลเมนูหลัก</h5>
                </div>
              </div>
            </div>

            <div class="setting-section">

              <div class="d-flex align-items-center justify-content-between mb-4">
                <div>
                  <p class="mb-1">ประวัติการรักษา</p>
                </div>
                <b-form-checkbox v-model="isShowMenuTreatmentHistory" switch size="lg" />
              </div>

              <hr class="my-4" />

              <div class="d-flex align-items-center justify-content-between mb-4">
                <div>
                  <p class="mb-1">ใบรับรองแพทย์</p>
                </div>
                <b-form-checkbox v-model="isShowMenuMedicalCertificate" switch size="lg" />
              </div>

              <hr class="my-4" />

              <div class="d-flex align-items-center justify-content-between mb-4">
                <p class="mb-1">ใบเสร็จ</p>
                <b-form-checkbox v-model="isShowMenuReceipt" switch size="lg" />
              </div>
            </div>

            <div class="text-right mt-4">
              <b-button variant="outline-secondary" size="lg" class="mr-2 px-4" @click="resetConfig">
                <i class="fas fa-undo mr-2"></i>
                รีเซ็ต
              </b-button>
              <b-button variant="primary" size="lg" class="px-4" @click="saveConfig">
                <i class="fas fa-save mr-2"></i>
                บันทึกการแสดงผลระบบ DentCare
              </b-button>
            </div>
          </b-card>
        </b-tab>
      </b-tabs>

      <!-- Dialogs and Loading -->
      <Dialog ref="Dialog"></Dialog>
      <Loading v-if="isLoading"></Loading>
    </div>
  </div>
</template>
<script>
import axios from "axios";
import { mapGetters, mapActions } from "vuex";

import { Datetime } from "vue-datetime";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import UploadAvatarForm from "@/components/form/UploadAvatar";

import moment from "moment";
import {
  required,
  requiredIf,
  numeric,
  minLength,
  maxLength,
  between,
  sameAs,
  not,
  helpers,
  email,
} from "vuelidate/lib/validators";
import { isEmpty } from "update/lib/utils";

const digits = number =>
  helpers.withParams(
    { type: "digits", value: number },
    value => !helpers.req(value) || helpers.len(value) == number
  );

export default {
  name: "Setting",
  components: {
    Dialog,
    Loading,
    Datetime,
    UploadAvatarForm,
  },
  data() {
    return {
      isLoading: false,
      clinic: {
        code: "",
        name: "",
        nameEn: "",
        detail: "",
        contentId: null,
      },
      defaultContentId: null,
      reqExtDocItems: [],
      appointment: null,
      readOnly: false,
      branchTitle: "",
      address: "",
      branchTitleEn: "",
      addressEn: "",
      telNumber: "",
      facebook: "",
      lineID: "",
      website: "",
      logo: null,
      dateTimeOpening: null,
      dateTimeClosing: null,
      period: null,
      roomNumber: null,
      defaultDF: 50,
      defaultOrthoDF: 50,
      hcode: null,
      isFHIRAccessEnabled: false,
      lineChannelId: "",
      lineChannelSecret: "",
      lineChannelAccessToken: "",
      showCopyPopover: false,

      isShowMenuTreatmentHistory: false,
      isShowMenuReceipt: false,
      isShowMenuMedicalCertificate: false,
      isSendLineWelcomeToNotification: false,
      isSendLineConfirmationAppointmentLink: false,
      isSendLineReceipt: false,

      isFeedbackValidation: false,
      isSendLineFeedback: false,
      feedbackUrl: "",

      // Room Management
      roomList: [],
      roomFields: [
        { key: "index", label: "#", class: "text-center", thStyle: { width: "50px" } },
        { key: "name", label: "ชื่อห้อง" },
        { key: "actions", label: "", class: "text-right", thStyle: { width: "120px" } },
      ],
      newRoomName: "",
      editingRoomId: null,
      editingRoomName: "",

      // Receipt Payment Method Management
      receiptPaymentList: [],
      paymentCategories: [],
      receiptPaymentFields: [
        { key: "index", label: "#", class: "text-center", thStyle: { width: "40px" } },
        { key: "name", label: "ชื่อ (ไทย)" },
        { key: "nameEn", label: "ชื่อ (EN)" },
        { key: "paymentCateId", label: "หมวดหมู่" },
        { key: "processingFee", label: "ค่าธรรมเนียม (%)", class: "text-center", thStyle: { width: "120px" } },
        { key: "actions", label: "", class: "text-right", thStyle: { width: "100px" } },
      ],
      dentCloudPaymentFields: [
        { key: "index", label: "#", class: "text-center", thStyle: { width: "40px" } },
        { key: "name", label: "ชื่อ (ไทย)" },
        { key: "nameEn", label: "ชื่อ (EN)" },
        { key: "paymentCateId", label: "หมวดหมู่" },
        { key: "processingFee", label: "ค่าธรรมเนียม (%)", class: "text-center", thStyle: { width: "120px" } },
        { key: "actions", label: "", class: "text-right", thStyle: { width: "100px" } },
      ],
      newPayment: { name: "", nameEn: "", paymentCateId: null, processingFee: null },
      editingPaymentId: null,
      editingPayment: { name: "", nameEn: "", paymentCateId: null, processingFee: null },
    };
  },
  validations: {
    period: { required },
    defaultDF: { required },
    defaultOrthoDF: { required },
  },
  created() {
    this.init();
  },
  watch: {},
  computed: {
    ...mapGetters({
      getBranchInfo: "moduleUser/getBranchInfo",
      getAppointmentDurationList: "moduleMaster/getAppointmentDurationList",
      isAccessMenu: "moduleUser/getIsAccessMenu",
    }),
    isManager() {
      return this.isAccessMenu(["MANAGER"]);
    },
    duration() {
      return moment
        .duration(
          this.getBranchInfo?.confApptDuration
            ? this.getBranchInfo.confApptDuration
            : "00:15"
        )
        .asMinutes();
    },
    openingTime() {
      return this.dateTimeOpening ? this.dateTimeOpening : "09:00";
    },
    closingTime() {
      return this.dateTimeClosing ? this.dateTimeClosing : "20:00";
    },
    periodOptions() {
      return this.getAppointmentDurationList.map(o => ({
        value: o.code,
        text: o.label,
      }));
    },
    paymentCategoryOptions() {
      const restrictedIds = [6, 7]; // 6 = KTB เป๋าตัง, 7 = บัตรทอง (NHSO)
      return this.paymentCategories
        .filter(cat => !restrictedIds.includes(cat.id))
        .filter(cat => !(cat.type === "Deposit" && this.hasActiveDeposit))
        .map(cat => ({
          value: cat.id,
          text: cat.name || cat.nameTh,
        }));
    },
    hasActiveDeposit() {
      const depositCat = this.paymentCategories.find(cat => cat.type === "Deposit");
      if (!depositCat) return false;
      return this.receiptPaymentList.some(
        pm => pm.paymentCateId === depositCat.id && !pm.del
      );
    },
    dentCloudPayments() {
      return this.receiptPaymentList
        .filter(pm => pm.dcPaymentType)
        .sort((a, b) => (a.del || 0) - (b.del || 0));
    },
    branchPayments() {
      return this.receiptPaymentList
        .filter(pm => !pm.dcPaymentType)
        .sort((a, b) => (a.del || 0) - (b.del || 0));
    },
  },
  methods: {
    ...mapActions({
      updateConfig: "moduleBranch/updateBranchConfig",
      fetchBranchByUrl: "moduleBranch/fetchBranchByUrl",
      fetchRoomListAction: "moduleBranch/fetchRoomList",
      createRoomAction: "moduleBranch/createRoom",
      updateRoomAction: "moduleBranch/updateRoom",
      deleteRoomAction: "moduleBranch/deleteRoom",
      fetchPaymentMethodList: "moduleUser/fetchPaymentMethodList",
      createReceiptPaymentAction: "moduleBranch/createReceiptPayment",
      updateReceiptPaymentAction: "moduleBranch/updateReceiptPayment",
      deleteReceiptPaymentAction: "moduleBranch/deleteReceiptPayment",
    }),
    init() {
      this.getClinic();
      this.fetchRooms();
      this.fetchReceiptPayments();
    },
    // Room Management Methods
    refreshBranchInfo() {
      this.fetchBranchByUrl({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
    },
    fetchRooms() {
      this.fetchRoomListAction({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      })
        .then((res) => {
          if (res.status === 200) {
            this.roomList = res.data.data || [];
          }
        })
        .catch(() => {
          this.roomList = [];
        });
    },
    addRoom() {
      const name = this.newRoomName.trim();
      if (!name) return;
      if (name.length > 100) {
        this.showDialogToast("warning", "ชื่อห้องต้องไม่เกิน 100 ตัวอักษร");
        return;
      }

      this.isLoading = true;
      this.createRoomAction({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        room: { name },
      })
        .then((res) => {
          if (res.status === 201 || res.status === 200) {
            this.showDialogToast("success", `สร้างห้อง "${name}" สำเร็จ`);
            this.newRoomName = "";
            this.fetchRooms();
            this.refreshBranchInfo();
          }
        })
        .catch(() => {
          this.showDialogToast("danger", "ไม่สามารถสร้างห้องได้");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    startEdit(room) {
      this.editingRoomId = room.id;
      this.editingRoomName = room.name;
    },
    cancelEdit() {
      this.editingRoomId = null;
      this.editingRoomName = "";
    },
    saveRoom(room) {
      const name = this.editingRoomName.trim();
      if (!name) {
        this.showDialogToast("warning", "กรุณากรอกชื่อห้อง");
        return;
      }
      if (name.length > 100) {
        this.showDialogToast("warning", "ชื่อห้องต้องไม่เกิน 100 ตัวอักษร");
        return;
      }

      this.isLoading = true;
      this.updateRoomAction({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        roomId: room.id,
        room: { name },
      })
        .then((res) => {
          if (res.status === 200) {
            this.showDialogToast("success", `แก้ไขห้อง "${name}" สำเร็จ`);
            this.cancelEdit();
            this.fetchRooms();
            this.refreshBranchInfo();
          }
        })
        .catch(() => {
          this.showDialogToast("danger", "ไม่สามารถแก้ไขห้องได้");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    deleteRoom(room) {
      // Check minimum 1 room
      if (this.roomList.length <= 1) {
        this.showDialogToast("warning", "ไม่สามารถลบได้ ต้องมีอย่างน้อย 1 ห้อง");
        return;
      }

      const roomName = room.name.trim();

      // Show confirmation with input field using SweetAlert2
      this.$refs.Dialog.fire({
        title: `ลบห้อง "${roomName}"?`,
        html: `<div class="text-left">
          <p class="text-danger font-weight-bold mb-2">คำเตือนสำคัญ!</p>
          <p>ถ้าลบห้องนี้ ข้อมูลนัดหมายของห้องนี้จะไม่แสดงผลในปฏิทินนัดหมายอีกต่อไป</p>
          <hr/>
          <p>พิมพ์ชื่อห้องเพื่อยืนยันการลบ:</p>
          <p class="text-center"><code class="bg-light px-3 py-2 rounded">${roomName}</code></p>
        </div>`,
        icon: "warning",
        input: "text",
        inputPlaceholder: roomName,
        showCancelButton: true,
        confirmButtonText: "ลบห้อง",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#d33",
        reverseButtons: true,
        inputValidator: (value) => {
          if (value.trim() !== roomName) {
            return `กรุณาพิมพ์ "${roomName}" เพื่อยืนยัน`;
          }
        },
      }).then((result) => {
        if (result.isConfirmed) {
          this.isLoading = true;
          this.deleteRoomAction({
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            roomId: room.id,
          })
            .then((res) => {
              if (res.status === 204 || res.status === 200) {
                this.showDialogToast("success", `ลบห้อง "${roomName}" สำเร็จ`);
                this.fetchRooms();
                this.refreshBranchInfo();
              }
            })
            .catch(() => {
              this.showDialogToast("danger", "ไม่สามารถลบห้องได้");
            })
            .finally(() => {
              this.isLoading = false;
            });
        }
      });
    },
    // Receipt Payment Method Management Methods
    fetchReceiptPayments() {
      this.fetchPaymentMethodList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      })
        .then((res) => {
          if (res.status === 200) {
            const result = res.data.result || [];

            // Extract categories (parent objects)
            this.paymentCategories = result.map(cat => ({
              id: cat.id,
              name: cat.name,
              type: cat.type,
            }));

            // Flatten payment methods from all categories
            this.receiptPaymentList = result.flatMap(cat =>
              (cat.mReceiptPayment || []).map(pm => ({
                ...pm,
                categoryName: cat.name,
              }))
            );
          }
        })
        .catch(() => {
          this.receiptPaymentList = [];
          this.paymentCategories = [];
        });
    },
    getCategoryName(cateId) {
      const category = this.paymentCategories.find(c => c.id === cateId);
      return category ? (category.name || category.nameTh) : "-";
    },
    getPaymentRowClass(item) {
      if (!item) return '';
      return item.del ? 'deleted-payment-row' : '';
    },
    addPayment() {
      const name = this.newPayment.name.trim();
      if (!name || !this.newPayment.paymentCateId) return;

      this.isLoading = true;
      this.createReceiptPaymentAction({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        receiptPayment: {
          name: name,
          nameEn: this.newPayment.nameEn?.trim() || null,
          paymentCateId: this.newPayment.paymentCateId,
          processingFee: this.newPayment.processingFee ? parseFloat(this.newPayment.processingFee) : null,
        },
      })
        .then((res) => {
          if (res.status === 201 || res.status === 200) {
            this.showDialogToast("success", `สร้างช่องทาง "${name}" สำเร็จ`);
            this.resetNewPayment();
            this.fetchReceiptPayments();
          }
        })
        .catch(() => {
          this.showDialogToast("danger", "ไม่สามารถสร้างช่องทางชำระเงินได้");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    resetNewPayment() {
      this.newPayment = { name: "", nameEn: "", paymentCateId: null, processingFee: null };
    },
    startPaymentEdit(payment) {
      this.editingPaymentId = payment.id;
      this.editingPayment = {
        name: payment.name,
        nameEn: payment.nameEn || "",
        paymentCateId: payment.paymentCateId,
        processingFee: payment.processingFee,
      };
    },
    cancelPaymentEdit() {
      this.editingPaymentId = null;
      this.editingPayment = { name: "", nameEn: "", paymentCateId: null, processingFee: null };
    },
    savePayment(payment) {
      const name = this.editingPayment.name?.trim();
      if (!name) {
        this.showDialogToast("warning", "กรุณากรอกชื่อช่องทางชำระเงิน");
        return;
      }
      if (!this.editingPayment.paymentCateId) {
        this.showDialogToast("warning", "กรุณาเลือกหมวดหมู่");
        return;
      }

      this.isLoading = true;
      this.updateReceiptPaymentAction({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        receiptPaymentId: payment.id,
        receiptPayment: {
          name: name,
          nameEn: this.editingPayment.nameEn?.trim() || null,
          paymentCateId: this.editingPayment.paymentCateId,
          processingFee: this.editingPayment.processingFee ? parseFloat(this.editingPayment.processingFee) : null,
        },
      })
        .then((res) => {
          if (res.status === 200) {
            this.showDialogToast("success", `แก้ไขช่องทาง "${name}" สำเร็จ`);
            this.cancelPaymentEdit();
            this.fetchReceiptPayments();
          }
        })
        .catch(() => {
          this.showDialogToast("danger", "ไม่สามารถแก้ไขช่องทางชำระเงินได้");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    deletePayment(payment) {
      const paymentName = payment.name.trim();

      this.$refs.Dialog.fire({
        title: `ลบช่องทาง "${paymentName}"?`,
        html: `<div class="text-left">
          <p class="text-danger font-weight-bold mb-2">คำเตือนสำคัญ!</p>
          <p>ถ้าลบช่องทางนี้ ข้อมูลจะไม่แสดงในรายงานรายได้อีกต่อไป</p>
          <hr/>
          <p>พิมพ์ชื่อช่องทางเพื่อยืนยันการลบ:</p>
          <p class="text-center"><code class="bg-light px-3 py-2 rounded">${paymentName}</code></p>
        </div>`,
        icon: "warning",
        input: "text",
        inputPlaceholder: paymentName,
        showCancelButton: true,
        confirmButtonText: "ลบช่องทาง",
        cancelButtonText: "ยกเลิก",
        confirmButtonColor: "#d33",
        reverseButtons: true,
        inputValidator: (value) => {
          if (value.trim() !== paymentName) {
            return `กรุณาพิมพ์ "${paymentName}" เพื่อยืนยัน`;
          }
        },
      }).then((result) => {
        if (result.isConfirmed) {
          this.isLoading = true;
          this.deleteReceiptPaymentAction({
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            receiptPaymentId: payment.id,
          })
            .then((res) => {
              if (res.status === 204 || res.status === 200) {
                this.showDialogToast("success", `ลบช่องทาง "${paymentName}" สำเร็จ`);
                this.fetchReceiptPayments();
              }
            })
            .catch(() => {
              this.showDialogToast("danger", "ไม่สามารถลบช่องทางชำระเงินได้");
            })
            .finally(() => {
              this.isLoading = false;
            });
        }
      });
    },
    resetConfig() {
      this.branchTitle = this.getBranchInfo.name || "";
      this.address = this.getBranchInfo.address || "";
      this.branchTitleEn = this.getBranchInfo.nameEn || "";
      this.addressEn = this.getBranchInfo.addressEn || "";
      this.telNumber = this.getBranchInfo.tel || "";
      this.facebook = this.getBranchInfo.facebook || "";
      this.lineID = this.getBranchInfo.line || "";
      this.website = this.getBranchInfo.website || "";
      this.hcode = this.getBranchInfo.hospitalCode || null;
      this.logo = this.getBranchInfo.logo || null;
      this.dateTimeOpening = this.getBranchInfo.confOpeningTime;
      this.dateTimeClosing = this.getBranchInfo.confClosingTime;
      this.period = this.getBranchInfo.confApptDuration;
      this.defaultDF = this.getBranchInfo.df ?? 50;
      this.defaultOrthoDF = this.getBranchInfo.dfPackage ?? 50;

      this.isFHIRAccessEnabled = this.getBranchInfo.isFHIRAccessEnabled;
      this.isShowMenuTreatmentHistory = this.getBranchInfo.isShowMenuTreatmentHistory || false;
      this.isShowMenuReceipt = this.getBranchInfo.isShowMenuReceipt || false;
      this.isShowMenuMedicalCertificate = this.getBranchInfo.isShowMenuMedicalCertificate || false;
      this.isSendLineWelcomeToNotification = this.getBranchInfo.isSendLineWelcomeToNotification || false;
      this.isSendLineConfirmationAppointmentLink = this.getBranchInfo.isSendLineConfirmationAppointmentLink || false;
      this.isSendLineFeedback = this.getBranchInfo.isSendLineFeedback || false;
      this.isSendLineReceipt = this.getBranchInfo.isSendLineReceipt || false;
      this.feedbackUrl = this.getBranchInfo.feedbackUrl || "";

      // Don't reset clinic data - keep the existing clinic information
      // This preserves the clinic name when reset is clicked
      this.getClinic(); // Re-fetch clinic data to preserve it

      this.lineChannelId = this.getBranchInfo.lineChannelId || "";
      this.lineChannelSecret = this.getBranchInfo.lineChannelSecret || "";
      this.lineChannelAccessToken =
        this.getBranchInfo.lineChannelAccessToken || "";
    },
    saveConfig() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      if (this.isSendLineFeedback) {
        if (isEmpty(this.feedbackUrl)) {
          this.isFeedbackValidation = true;
          return;
        }
      }
      var configData = {
        name: this.branchTitle,
        address: this.address,
        nameEn: this.branchTitleEn,
        addressEn: this.addressEn,
        tel: this.telNumber,
        facebook: this.facebook,
        line: this.lineID,
        website: this.website,
        confOpeningTime: this.formatTime(this.dateTimeOpening),
        confClosingTime: this.formatTime(this.dateTimeClosing),
        confApptDuration: this.period,
        df: Number(this.defaultDF),
        dfPackage: Number(this.defaultOrthoDF),
        id: this.getBranchInfo.id,
        isFHIRAccessEnabled: this.isFHIRAccessEnabled,
        lineChannelId: this.lineChannelId,
        lineChannelSecret: this.lineChannelSecret,
        lineChannelAccessToken: this.lineChannelAccessToken,

        isShowMenuTreatmentHistory: this.isShowMenuTreatmentHistory,
        isShowMenuReceipt: this.isShowMenuReceipt,
        isShowMenuMedicalCertificate: this.isShowMenuMedicalCertificate,
        isSendLineWelcomeToNotification: this.isSendLineWelcomeToNotification,
        isSendLineConfirmationAppointmentLink: this.isSendLineConfirmationAppointmentLink,
        isSendLineFeedback: this.isSendLineFeedback,
        isSendLineReceipt: this.isSendLineReceipt,
        feedbackUrl: this.feedbackUrl,
      };

      this.isLoading = true;
      this.updateConfig({
        data: configData,
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      })
        .then(res => {
          if (res.status == 200) {
            this.showDialogToast("success", `อัพเดตข้อมูลติดต่อสาขาสำเร็จ`);
          }
        })
        .catch(res => {
          this.showDialogToast("danger", `ไม่สามารถอัพเดตข้อมูลติดต่อสาขาได้`);
        })
        .finally(() => {
          this.isLoading = false;
          this.isFeedbackValidation = false;
        });
    },
    changeFHIR(e) {
      this.resetConfig();

      this.isFHIRAccessEnabled = e;
      var configData = {
        name: this.branchTitle,
        address: this.address,
        nameEn: this.branchTitleEn,
        addressEn: this.addressEn,
        tel: this.telNumber,
        facebook: this.facebook,
        line: this.lineID,
        website: this.website,
        confOpeningTime: this.formatTime(this.dateTimeOpening),
        confClosingTime: this.formatTime(this.dateTimeClosing),
        confApptDuration: this.period,
        df: Number(this.defaultDF),
        dfPackage: Number(this.defaultOrthoDF),
        id: this.getBranchInfo.id,
        isFHIRAccessEnabled: this.isFHIRAccessEnabled,
        isShowMenuTreatmentHistory: this.isShowMenuTreatmentHistory,
        isShowMenuReceipt: this.isShowMenuReceipt,
        isShowMenuMedicalCertificate: this.isShowMenuMedicalCertificate,
        isSendLineWelcomeToNotification: this.isSendLineWelcomeToNotification,
        isSendLineConfirmationAppointmentLink: this.isSendLineConfirmationAppointmentLink,
        isSendLineFeedback: this.isSendLineFeedback,
        isSendLineReceipt: this.isSendLineReceipt,
        feedbackUrl: this.feedbackUrl,
      };

      this.isLoading = true;
      this.updateConfig({
        data: configData,
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      })
        .then(res => {
          if (res.status == 200) {
            this.showDialogToast("success", `อัพเดตข้อมูลติดต่อสาขาสำเร็จ`);
          }
        })
        .catch(res => {
          this.showDialogToast("danger", `ไม่สามารถอัพเดตข้อมูลติดต่อสาขาได้`);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    uploadAvatarCallback(state, contentId) {
      switch (state) {
        case "uploading":
          this.isLoading = true;
          break;
        case "callback":
          this.isLoading = false;
          this.checkPatient();
          break;
        case "delete":
          this.clinic.logo = contentId || null;
          break;
        case "reset":
          this.clinic.logo = contentId;
          break;
        case "success":
          this.isLoading = false;
          this.clinic.logo = contentId;
          this.checkPatient();
          break;
        case "fail":
          this.isLoading = false;
          this.showDialogToast("danger", `ไม่สามารถอัพโหลดรูปโปรไฟล์ได้`);
          this.checkPatient();
          break;
        default:
      }
    },
    resetForm() {
      this.$refs.UploadAvatar.reset("");
      this.setDefultClinic();
      this.$v.$reset();
    },
    saveSetting() {
      this.$refs.UploadAvatar.submit();
    },
    async checkPatient() {
      this.saveCustomer();
    },
    saveCustomer() {
      this.$refs.Dialog.showAlertConfirm(
        "แก้ไขข้อมูลคลีนิค ?",
        "คุณต้องการแก้ไข รายละเอียดข้อมูลคลีนิค"
      ).then(res => {
        if (res.isConfirmed) {
          var clinicUrl = this.$route.params.clinicUrl;
          var branchUrl = this.$route.params.branchUrl;
          axios
            .put(`/api/clinic/${clinicUrl}`, this.clinic)
            .then(res => {
              if (res.status == "200") {
                this.fetchBranchByUrl({
                  clinicUrl: this.$route.params.clinicUrl,
                  branchUrl: this.$route.params.branchUrl,
                });
              }
              this.isLoading = false;
            })
            .catch(() => {
              this.isLoading = false;
            });
        } else {
          this.isLoading = false;
        }
      });
    },
    getClinic() {
      var clinicUrl = this.$route.params.clinicUrl;
      axios.get(`/api/clinic/${clinicUrl}`).then(res => {
        this.clinic = Object.assign({}, res.data);
        this.defaultContentId = this.clinic.logo;
      });
    },
    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
    formatTime(e) {
      if (e.length <= 5) return e;
      return moment(e).format("HH:mm");
    },
    async copyToClipboard(text, itemName) {
      try {
        await navigator.clipboard.writeText(text);
        this.showCopyPopover = true;
        setTimeout(() => {
          this.showCopyPopover = false;
        }, 2000);
      } catch (error) {
        console.error("Failed to copy text:", error);
        this.showDialogToast("danger", "ไม่สามารถคัดลอกได้");
      }
    },
  },
  mounted() {
    this.resetConfig();
  },
  updated() { },
  beforeDestroy() {
    //เช็คเว่า edited?
  },
  destroyed() { },
};
</script>
<style scoped>
/* General Layout */
.view-setting {
  background-color: #f8f9fa;
  min-height: 100vh;
  padding: 1.5rem;
}

.content {
  max-width: 1200px;
  margin: 0 auto;
}

/* Header Styles */
.settings-header {
  background: white;
  padding: 2rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.page-title {
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.page-title i {
  color: #3490dc;
}

/* Tab Styles */
.custom-tabs .nav-link {
  color: #6c757d;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  border: none;
  border-bottom: 3px solid transparent;
  transition: all 0.3s ease;
}

.custom-tabs .nav-link:hover {
  color: #3490dc;
  background-color: #f8f9fa;
}

.custom-tabs .nav-link.active {
  color: #3490dc;
  background-color: transparent;
  border-bottom-color: #3490dc;
}

/* Card Styles */
.card {
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Section Styles */
.section-title {
  color: #2c3e50;
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
}

.section-title i {
  color: #3490dc;
  font-size: 1rem;
}

.setting-section {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
}

/* Form Styles */
.form-group label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.5rem;
}

.form-control,
.custom-select {
  border: 1px solid #e1e5eb;
  border-radius: 0.375rem;
  transition: all 0.3s ease;
}

.form-control:focus,
.custom-select:focus {
  border-color: #3490dc;
  box-shadow: 0 0 0 0.2rem rgba(52, 144, 220, 0.25);
}

.form-control[disabled] {
  background-color: #f8f9fa;
  cursor: not-allowed;
}

/* Input Group Styles */
.input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #e1e5eb;
  color: #6c757d;
}

.input-group-text i {
  width: 1.2rem;
  text-align: center;
}

/* Feedback URL Clear Button */
.feedback-url-group {
  position: relative;
}

.feedback-url-wrapper {
  position: relative;
}

.feedback-url-input {
  padding-right: 2.5rem;
}

.feedback-clear-button {
  position: absolute;
  top: 50%;
  right: 0.25rem;
  transform: translateY(-50%);
  padding: 0 0.5rem;
  z-index: 2;
}

.feedback-clear-button-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.feedback-clear-icon {
  width: 18px;
  height: 18px;
}

/* Button Styles */
.btn {
  font-weight: 500;
  transition: all 0.3s ease;
  border-radius: 0.375rem;
}

.btn-primary {
  background-color: #3490dc;
  border-color: #3490dc;
}

.btn-primary:hover {
  background-color: #2779bd;
  border-color: #2779bd;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(52, 144, 220, 0.3);
}

.btn-outline-danger:hover {
  transform: translateY(-1px);
}

/* Action Buttons */
.action-buttons {
  background: white;
  padding: 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* LINE Integration Styles */
.line-integration-header {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.5rem;
}

/* Support Information Styles */
.support-info-header {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.support-code-container {
  transition: all 0.3s ease;
}

.support-code-container:hover {
  transform: translateY(-1px);
}

.support-code-container .form-control {
  font-family: "Courier New", monospace;
  letter-spacing: 1px;
}

.support-combined-section {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.5rem;
  border: 2px dashed #dee2e6;
}

.contact-instructions {
  border-left: 4px solid #3490dc;
}

.contact-instructions ul {
  padding-left: 1.2rem;
}

.contact-instructions li {
  margin-bottom: 0.25rem;
  color: #6c757d;
}

/* Copy Component Wrapper Styles */
.copy-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-left: 1px solid #dee2e6;
}

.copy-wrapper-large {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  background-color: #007bff;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
}

.copy-wrapper-large:hover {
  background-color: #0056b3;
  transform: translateY(-1px);
}

.copy-wrapper-large:active {
  transform: translateY(0);
  background-color: #004085;
}

.copy-wrapper-large span {
  font-weight: 500;
  pointer-events: none;
}

.copy-wrapper-large i {
  pointer-events: none;
}

/* LINE Contact Section */
.contact-line {
  transition: all 0.3s ease;
}

.contact-line:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Support Input Field Height Fix */
.support-input-field {
  height: calc(2.875rem + 2px);
  line-height: 2.875rem;
}

.support-input-addon {
  height: calc(2.875rem + 2px);
  display: flex;
  align-items: center;
}

.support-input-addon .copy-wrapper-large {
  height: 100%;
  display: flex;
  align-items: center;
}

/* Switch Styles */
.custom-control-input:checked~.custom-control-label::before {
  background-color: #3490dc;
  border-color: #3490dc;
}

.custom-switch.b-custom-control-lg .custom-control-label::before {
  border-radius: 1rem;
}

/* Alert Styles */
.alert-info {
  background-color: #e3f2fd;
  border-color: #90caf9;
  color: #1565c0;
}

/* Responsive */
@media (max-width: 768px) {
  .view-setting {
    padding: 0.5rem;
  }

  .settings-header {
    padding: 1.5rem;
  }

  .custom-tabs .nav-link {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }

  .action-buttons {
    text-align: center !important;
  }

  .action-buttons .btn {
    display: block;
    width: 100%;
    margin-bottom: 0.5rem;
  }
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card {
  animation: fadeIn 0.3s ease-out;
}

/* Form Validation Styles */
.is-invalid {
  border-color: #dc3545;
}

.is-invalid:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.invalid-feedback {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #dc3545;
}

/* Room Table Styles */
.room-table {
  margin-bottom: 0;
}

.room-table th {
  border-top: none;
  font-weight: 600;
  color: #495057;
}

.room-table td {
  vertical-align: middle;
}

.room-table .btn-sm {
  padding: 0.25rem 0.5rem;
}

/* Deleted Payment Row Styles */
.receipt-payment-table .deleted-payment-row {
  opacity: 0.5;
  background-color: #f8f9fa;
}

.receipt-payment-table .deleted-payment-row td {
  text-decoration: line-through;
  color: #6c757d;
}

.receipt-payment-table .deleted-payment-row td:last-child {
  text-decoration: none;
}
</style>
